/**
 * Browser geolocation with fallback and user-friendly errors.
 * Desktop often fails with enableHighAccuracy when Wi‑Fi positioning is unavailable.
 */

const GEO_ERRORS = {
  1: 'Location access was denied. Allow location for this site in your browser settings, then try again.',
  2: 'Could not detect your position. On a computer, turn on Location Services in system settings, or try on a phone with GPS.',
  3: 'Finding your location took too long. Check your connection and try again.',
};

export function isGeolocationSupported() {
  return typeof navigator !== 'undefined' && !!navigator.geolocation;
}

export function isSecureContextForGeolocation() {
  if (typeof window === 'undefined') return true;
  if (window.isSecureContext) return true;
  const host = window.location?.hostname || '';
  return host === 'localhost' || host === '127.0.0.1';
}

export function geolocationErrorMessage(error) {
  if (!error) {
    return 'Unable to get your location. Please try again.';
  }
  if (GEO_ERRORS[error.code]) return GEO_ERRORS[error.code];
  const msg = String(error.message || '').trim();
  if (/unavailable/i.test(msg)) {
    return GEO_ERRORS[2];
  }
  if (/denied|permission/i.test(msg)) {
    return GEO_ERRORS[1];
  }
  if (/timeout/i.test(msg)) {
    return GEO_ERRORS[3];
  }
  return msg || 'Unable to get your location. Please try again.';
}

function getCurrentPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 * Resolve { latitude, longitude } with high-accuracy attempt, then network/cell fallback.
 */
export async function getUserPosition() {
  if (!isGeolocationSupported()) {
    throw new Error('Location is not supported on this device or browser.');
  }

  if (!isSecureContextForGeolocation()) {
    throw new Error(
      'Location requires a secure connection (HTTPS). Open the app over HTTPS to use your current location.'
    );
  }

  const attempts = [
    { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 },
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
  ];

  let lastError = null;
  for (const opts of attempts) {
    try {
      const pos = await getCurrentPosition(opts);
      const { latitude, longitude } = pos.coords;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw Object.assign(new Error('Invalid coordinates'), { code: 2 });
      }
      return { latitude, longitude, accuracy: pos.coords.accuracy };
    } catch (err) {
      lastError = err;
      if (err?.code === 1) break;
    }
  }

  throw lastError || new Error('Unable to get your location.');
}

/** Legacy numeric ids — prefer API service areas (slug + lat/lng). */
export const CITY_CENTROIDS = {
  '1': { lat: 28.6139, lng: 77.209, label: 'Delhi (NCR)' },
  'delhi-ncr': { lat: 28.6139, lng: 77.209, label: 'Delhi (NCR)' },
  '2': { lat: 28.4595, lng: 77.0266, label: 'Gurugram' },
  gurugram: { lat: 28.4595, lng: 77.0266, label: 'Gurugram' },
  '3': { lat: 28.5355, lng: 77.391, label: 'Noida' },
  noida: { lat: 28.5355, lng: 77.391, label: 'Noida' },
  '4': { lat: 19.076, lng: 72.8777, label: 'Mumbai' },
  mumbai: { lat: 19.076, lng: 72.8777, label: 'Mumbai' },
  '5': { lat: 12.9716, lng: 77.5946, label: 'Bangalore' },
  bangalore: { lat: 12.9716, lng: 77.5946, label: 'Bangalore' },
  '6': { lat: 13.0827, lng: 80.2707, label: 'Chennai' },
  chennai: { lat: 13.0827, lng: 80.2707, label: 'Chennai' },
  '7': { lat: 17.385, lng: 78.4867, label: 'Hyderabad' },
  hyderabad: { lat: 17.385, lng: 78.4867, label: 'Hyderabad' },
  '8': { lat: 22.5726, lng: 88.3639, label: 'Kolkata' },
  kolkata: { lat: 22.5726, lng: 88.3639, label: 'Kolkata' },
};

/** Map API slugs to legacy demo hospital locationId keys. */
export const SLUG_TO_LEGACY_LOCATION_ID = {
  'delhi-ncr': '1',
  gurugram: '2',
  noida: '3',
  mumbai: '4',
  bangalore: '5',
  chennai: '6',
  hyderabad: '7',
  kolkata: '8',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
};

export function resolveLegacyLocationId(selectedLocation) {
  if (!selectedLocation) return '1';
  const key = String(selectedLocation.slug || selectedLocation.id || '').trim();
  if (key === 'current') return '1';
  return SLUG_TO_LEGACY_LOCATION_ID[key] || (/^\d$/.test(key) ? key : '1');
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Nearest bookable service area from API list (lat/lng on each area). */
export function nearestServiceArea(areas, latitude, longitude) {
  if (!Array.isArray(areas) || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  let best = null;
  let bestDist = Infinity;
  for (const area of areas) {
    if (!area?.bookable) continue;
    const lat = Number(area.lat);
    const lng = Number(area.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const d = haversineKm(latitude, longitude, lat, lng);
    if (d < bestDist) {
      bestDist = d;
      best = area;
    }
  }
  return best;
}

export function getApproximateCoordsForCity(locationId, areas) {
  const key = String(locationId || '').trim();
  if (Array.isArray(areas) && areas.length) {
    const fromList = areas.find((a) => a.id === key || a.slug === key);
    if (fromList && Number.isFinite(fromList.lat) && Number.isFinite(fromList.lng)) {
      return { lat: fromList.lat, lng: fromList.lng, label: fromList.name || key };
    }
  }
  const entry = CITY_CENTROIDS[key];
  if (!entry) return null;
  return { lat: entry.lat, lng: entry.lng, label: entry.label };
}
